// ozg-storage.js
import Formio from 'formiojs/Formio'
import config from '@/config/config.js'

/*
// Basis-API-URL (für Download/Delete & alle anderen API-Calls)
const API_BASE = (config.API_URL && config.API_URL.trim()) || '';
const API = API_BASE.replace(/\/+$/, '') + '/api/';     // <-- hier fest /api/ anhängen
const UPLOAD_BASE = ((config.API_UPLOAD_URL && config.API_UPLOAD_URL.trim()) || API).replace(/\/+$/, '/') ;
*/

const API_BASE = (config.API_URL && config.API_URL.trim()) || ''

// Für dev04/dev05 doppelte /api-Logik beibehalten:
// Wenn config.API_URL z. B. "https://ozgndev04.itom.report/api/" ist,
// dann wird daraus bewusst "https://ozgndev04.itom.report/api/api/"
const API = API_BASE.replace(/\/+$/, '') + '/api/'
const UPLOAD_BASE = ((config.API_UPLOAD_URL && config.API_UPLOAD_URL.trim()) || API).replace(/\/+$/, '/')

const UPLOAD_URL = UPLOAD_BASE + 'upload'
const DOWNLOAD_URL = API + 'storage/download'
const DELETE_URL = API + 'storage'

const COMMUNITY_CONTEXT_URL = API + 'communityContext'

function getParam (name) {
  return new URL(window.location.href).searchParams.get(name)
}

console.log('[OZG-STORAGE] VERSION 2026-07-08-1605 NO FRONTEND DUPLICATE BLOCK')

let cachedContextToken = null

async function getCommunityContextToken (headers = {}) {
  if (cachedContextToken) return cachedContextToken

  const isLocal =
    /(^localhost$)|(^127\.0\.0\.1$)/i.test(window.location.hostname) ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.endsWith('.local')

  if (isLocal) {
    cachedContextToken = 'LOCAL_DEV'
    return cachedContextToken
  }

  const c =
    getParam('c') ||
    getParam('communityIdentifier') ||
    getParam('community') ||
    getParam('cid') ||
    headers['X-Community-Identifier'] ||
    headers['x-community-identifier'] ||
    headers['X-Community-Context'] ||
    headers['x-community-context'] ||
    null

  if (!c) {
    throw new Error('CommunityIdentifier fehlt in URL und Headern.')
  }

  const res = await fetch(`${COMMUNITY_CONTEXT_URL}?c=${encodeURIComponent(c)}`, {
    method: 'GET',
    credentials: 'include'
  })

  const text = await res.text()
  if (!res.ok) {
    throw new Error(`CommunityContext fehlgeschlagen (${res.status}): ${text}`)
  }

  let json
  try {
    json = JSON.parse(text)
  } catch (e) {
    throw new Error(`CommunityContext liefert kein gültiges JSON: ${text}`)
  }

  const token =
    json.communityContextToken ||
    json.token ||
    json.contextToken ||
    json.value ||
    json.communityToken ||
    null

  if (!token) {
    throw new Error(`CommunityContextToken fehlt in der Antwort: ${text}`)
  }

  cachedContextToken = token
  return cachedContextToken
}


// SHA-256 Hash eines File-Objekts berechnen (Hex-String)
async function sha256FileHex (file) {
  try {
    if (!file || !file.arrayBuffer || !window.crypto || !window.crypto.subtle) {
      return null // Hashing nicht möglich -> Duplikat-Erkennung deaktiviert
    }

    const buf = await file.arrayBuffer()
    const hashBuf = await window.crypto.subtle.digest('SHA-256', buf)
    const bytes = new Uint8Array(hashBuf)

    let hex = ''
    for (const b of bytes) {
      hex += b.toString(16).padStart(2, '0')
    }

    return hex
  } catch (e) {
    // console.warn('[OZG] sha256FileHex fehlgeschlagen', e)
    return null
  }
}

// Hilfsfunktion: Uploadfeld-Key aus Form.io-Kontext ableiten.
// Wir loggen später einmal, ob options/dir bei euch den richtigen Wert liefern.
function resolveUploadFieldKey (dir, options = {}) {
  return (
    options?.component?.key ||
    options?.fileComponent?.key ||
    options?.componentKey ||
    options?.key ||
    dir ||
    ''
  )
}

// Hilfsfunktion: Form-Kontext für Upload/Rate-Limit/Quota
function resolveOzgFormContext (dir, options = {}) {
  const formSessionId =
    window.sessionId ||
    window.OZG_BACKEND_FORM_SESSION_ID ||
    ''

  const formId =
    window.formCommunityDTO?.formId ||
    window.formId ||
    window.currentForm?.formId ||
    ''

  const communityIdentifier =
    window.formCommunityDTO?.communityIdentifier ||
    window.communityData?.communityIdentifier ||
    ''

  const uploadFieldKey = resolveUploadFieldKey(dir, options)

  return {
    formSessionId: String(formSessionId || ''),
    formId: String(formId || ''),
    communityIdentifier: String(communityIdentifier || ''),
    uploadFieldKey: String(uploadFieldKey || '')
  }
}

function resolveDeleteKey (file) {
  return (
    file?.key ||
    file?.data?.key ||
    file?.filePath ||
    file?.data?.filePath ||
    file?.path ||
    ''
  )
}

function resolveFileHash (file) {
  return (
    file?.hash ||
    file?.data?.hash ||
    file?.fileHash ||
    file?.data?.fileHash ||
    ''
  )
}



// ========== Upload per XHR ==========
// Zweck: Upload über XHR (Progressbar), plus serverseitige Fehler sauber in UI bringen
function xhrPost (url, formData, headers = {}, progressCb) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)
    xhr.withCredentials = true

    Object.entries(headers).forEach(([k, v]) => {
      if (v != null && String(v).length) xhr.setRequestHeader(k, v)
    })

    if (typeof progressCb === 'function' && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) progressCb(e.loaded / e.total)
      }
    }

    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) return

      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch (e) {
          reject('Technischer Fehler: Serverantwort ist kein gültiges JSON.')
        }
      } else {
        let errPayload = null
        const ct = xhr.getResponseHeader('Content-Type') || ''

        if (ct.includes('application/json')) {
          try { errPayload = JSON.parse(xhr.responseText) } catch {}
        }

        if (errPayload && errPayload.messageDe) {
          window.__ozgLastUploadError = {
            code: errPayload.code,
            reqId: errPayload.reqId,
            details: errPayload.details || null
          }

          return reject(errPayload.messageDe)
        }

        const fallbackByStatus = {
          400: 'Ungültiger Upload. Bitte Dateityp und Anzahl prüfen.',
          401: 'Nicht angemeldet. Bitte erneut einloggen.',
          413: 'Datei oder Gesamtgröße zu groß.',
          415: 'Ungültiges Upload-Format. Erwartet wird multipart/form-data.',
          429: 'Zu viele Uploads in kurzer Zeit. Bitte später erneut versuchen.',
          500: 'Serverfehler beim Upload. Bitte später erneut versuchen.'
        }

        const msg = fallbackByStatus[xhr.status] || `Upload fehlgeschlagen (${xhr.status}).`
        reject(msg)
      }
    }

    xhr.onerror = () => reject('Netzwerkfehler beim Upload.')
    xhr.send(formData)
  })
}

// ========== Auto-Patch: File.deleteFile soll immer Provider verwenden ==========
// Zweck: Form.io verwendet manchmal intern nicht deinen Provider beim Löschen.
// Dieser Patch zwingt Delete über fs.deleteFile(...) damit dein Backend sauber löscht.
;(function autoPatchDelete () {
  function apply () {
    const File = window.Formio?.Components?.components?.file
    if (!File?.prototype || File.prototype.__ozgPatched) return !!File?.prototype

    const orig = File.prototype.deleteFile

    File.prototype.deleteFile = function (fi) {
      try {
        const fs = this && this.fileService
        if (fs && typeof fs.deleteFile === 'function') {
          return fs.deleteFile(fi, this.component?.options || {})
        }
      } catch (e) {
        // console.error('[OZG] File.patch runtime error', e)
      }

      return orig ? orig.call(this, fi) : Promise.resolve()
    }

    File.prototype.__ozgPatched = true
    return true
  }

  if (!apply()) {
    let n = 0
    const t = setInterval(() => {
      if (apply() || ++n > 50) clearInterval(t)
    }, 200)
  }
})()

// ========== OZG Storage Provider ==========
class OZGStorageProvider {
  static title = 'OZG Storage'
  static name = 'ozg'

  constructor (options = {}) {
    this.options = options || {}
  }

  async uploadFile (file, fileName, dir, progressCallback, url, options = {}) {
    const originalName = (file && file.name) ? file.name : (fileName || 'upload.bin')

    // --------- Duplikat-Erkennung nach Dateiinhalt (Frontend) ---------
    // Wichtig:
    // - NICHT sofort speichern.
    // - Erst prüfen.
    // - Erst nach erfolgreichem Server-Upload in seenUploadContentHashes eintragen.
    const hash = await sha256FileHex(file)

// WICHTIG:
// Das Frontend blockiert keine Duplikate mehr.
// Der Hash wird nur berechnet und später per Header ans Backend übergeben.
// Die echte Prüfung "gleiche Datei in zwei aktiven Uploadfeldern"
// machen wir serverseitig über sessionId + formId + uploadFieldKey + fileHash.
if (hash) {
  try {
    window.__ozgLastUploadHash = hash
  } catch (e) {}
}

    const formData = new FormData()
    formData.append('file', file, originalName)

    // WICHTIG: headers kopieren, nicht Original mutieren
    const headers = (options && options.headers) ? { ...options.headers } : {}

    // Context-Token holen und mitsenden
    const token = await getCommunityContextToken(headers)
    headers['X-Community-Context'] = token
    headers['X-OZG-Original-Filename'] = encodeURIComponent(originalName)

    // Neuer OZG-Kontext für sessionbasiertes Rate-Limit/Quota/Duplicate-Tracking
    const ozgContext = resolveOzgFormContext(dir, options)

    headers['X-OZG-Form-Session'] = ozgContext.formSessionId
    headers['X-OZG-Form-Id'] = ozgContext.formId
    headers['X-OZG-Community-Identifier'] = ozgContext.communityIdentifier
    headers['X-OZG-Upload-Field'] = ozgContext.uploadFieldKey
    headers['X-OZG-File-Hash'] = hash || ''

    // DEBUG NUR FÜR ERSTEN TEST AKTIVIEREN:
    // Danach wieder auskommentieren, sonst spammt es die Browser-Konsole.
    /*
    console.group('[OZG][UPLOAD] context')
    console.log('dir:', dir)
    console.log('url:', url)
    console.log('options:', options)
    console.log('window.sessionId:', window.sessionId)
    console.log('window.formCommunityDTO:', window.formCommunityDTO)
    console.log('ozgContext:', ozgContext)
    console.log('hash:', hash)
    console.groupEnd()
    */

    // Optional: CaseId (wenn vorhanden)
    // 1) bevorzugt: URL ?id=...
    const caseIdFromUrl = getParam('id')
    // 2) fallback: X-Form-Identifier wenn du nichts anderes hast
    const caseIdFallback = headers['X-Form-Identifier']

    if (caseIdFromUrl) headers['X-Case-Identifier'] = caseIdFromUrl
    else if (caseIdFallback) headers['X-Case-Identifier'] = caseIdFallback

    const raw = await xhrPost(UPLOAD_URL, formData, headers, progressCallback)

    const normalizedFilePath = raw?.filePath
      ? String(raw.filePath).replace(/^~\//, '/')
      : null

    const normalizedUrl = raw?.url
      ? String(raw.url).replace(/^~\//, '/')
      : null

    const out = {
      storage: 'ozg',
      name: raw?.name || originalName,
      originalName: originalName,
      url: normalizedUrl || (raw?.key ? `${DOWNLOAD_URL}?key=${encodeURIComponent(raw.key)}` : null),
      size: raw?.size ?? file.size,
      type: raw?.type || file.type || 'application/octet-stream',
      key: raw?.key || null,
      filePath: normalizedFilePath,

      // Zusatzwerte, damit Backend/Delete/Debug später mehr Kontext hat
      hash: hash || null,
      uploadFieldKey: ozgContext.uploadFieldKey,
      formSessionId: ozgContext.formSessionId,
      formId: ozgContext.formId,

      data: {
        key: raw?.key || null,
        filePath: normalizedFilePath,
        hash: hash || null,
        uploadFieldKey: ozgContext.uploadFieldKey,
        formSessionId: ozgContext.formSessionId,
        formId: ozgContext.formId
      }
    }

  

    if (typeof progressCallback === 'function') progressCallback(1)

    return out
  }

  // Download unverändert
  downloadFile (file) {
    const u = file?.url || (file?.key ? `${DOWNLOAD_URL}?key=${encodeURIComponent(file.key)}` : null)
    return Promise.resolve({ url: u })
  }
async deleteFile (file, options = {}) {
  const key = resolveDeleteKey(file)

  if (!key) {
    console.warn('[OZG-STORAGE] DELETE skipped: no key', file)
    return Promise.resolve()
  }

  const headers = {
    'Content-Type': 'application/json'
  }

  try {
    const token = await getCommunityContextToken(headers)
    headers['X-Community-Context'] = token
  } catch (e) {
    console.warn('[OZG-STORAGE] DELETE community context konnte nicht gesetzt werden', e)
  }

  const ozgContext = resolveOzgFormContext(
    file?.uploadFieldKey || file?.data?.uploadFieldKey || '',
    options
  )

  const formSessionId =
    ozgContext.formSessionId ||
    file?.formSessionId ||
    file?.data?.formSessionId ||
    ''

  const formId =
    ozgContext.formId ||
    file?.formId ||
    file?.data?.formId ||
    ''

  const uploadFieldKey =
    ozgContext.uploadFieldKey ||
    file?.uploadFieldKey ||
    file?.data?.uploadFieldKey ||
    ''

  const fileHash = resolveFileHash(file)

  headers['X-OZG-Form-Session'] = formSessionId
  headers['X-OZG-Form-Id'] = formId
  headers['X-OZG-Upload-Field'] = uploadFieldKey
  headers['X-OZG-File-Hash'] = fileHash

  const body = {
    Key: key,
    FileHash: fileHash,
    UploadFieldKey: uploadFieldKey,
    FormSessionId: formSessionId,
    FormId: formId
  }

  const res = await fetch(DELETE_URL, {
    method: 'DELETE',
    headers: headers,
    credentials: 'include',
    body: JSON.stringify(body)
  })

  const text = await res.text()

  if (!res.ok) {
    const err = new Error(`delete failed ${res.status}: ${text}`)
    console.error('[OZG-STORAGE] DELETE error', err)
    throw err
  }

  return true
}
}

OZGStorageProvider.title = 'OZG Storage'
OZGStorageProvider.name = 'ozg'

Formio.Providers.addProvider('storage', 'ozg', OZGStorageProvider)

export { OZGStorageProvider }
export default OZGStorageProvider